import { Link } from 'react-router-dom';
import { api, type AttentionResponse } from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';

/**
 * Caixa de atenção do dono.
 *
 * Duas decisões de desenho que a especificação exige e que são fáceis de errar:
 *
 * 1. **A caixa é derivada** (Back Office §17). Reconhecer não faz o item sair:
 *    o sistema continua pausado depois de alguém dizer "eu vi". O item
 *    reconhecido fica aqui, marcado — e é isso que AT-156 mede.
 *
 * 2. **Nada é agrupado por tipo de objeto.** A ordem é por severidade, porque a
 *    pergunta do dono é "o que é pior", não "quantas escalações existem".
 */

const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Crítico',
  high: 'Alto',
  medium: 'Médio',
  low: 'Baixo',
  info: 'Informativo',
};

const SEVERITY_CLASS: Record<string, string> = {
  critical: 'border-l-4 border-l-danger bg-danger-surface/60',
  high: 'border-l-4 border-l-warn bg-warn-surface/50',
  medium: 'border-l-4 border-l-warn bg-warn-surface/40',
  low: 'border-l-4 border-l-line-strong',
  info: 'border-l-4 border-l-line',
};

/** Rótulo em português da condição. A pessoa não lê nome de tabela. */
const CONDITION_LABEL: Record<string, string> = {
  EscalationRecord: 'Escalação',
  PauseState: 'Pausa ativa',
  HoldState: 'Hold ativo',
  ContentItem: 'Conteúdo travado',
  PublicationRecord: 'Publicação ambígua',
  ExceptionRecord: 'Exceção de política',
  CapabilityRegistry: 'Capacidade externa',
};

export function Attention() {
  const resource = useResource<AttentionResponse>(() => api.attention(), []);

  if (resource.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Pausas e escalação</h1>
        <p className="mt-2 text-ink-muted">Derivando do estado do domínio…</p>
      </div>
    );
  }

  if (resource.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Pausas e escalação</h1>
        {/*
          Erro NUNCA vira "nenhum item pendente". Uma caixa vazia por falha de
          leitura seria a pior tela do produto: o dono concluiria que está tudo
          em ordem justamente quando não se sabe.
        */}
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível derivar a caixa de atenção.</p>
          <p className="mt-1">{resource.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Isto <strong>não</strong> significa que não há pendências. Significa que não sabemos.
            Código: {resource.code}
            {resource.correlationId ? ` · correlação: ${resource.correlationId}` : ''}
          </p>
        </div>
      </div>
    );
  }

  const { data } = resource;
  const naoVisiveis = data.total - data.visible_to_actor;

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Pausas e escalação</h1>
        <p className="mt-1 text-ink-muted">
          Derivada do estado do domínio às{' '}
          <time dateTime={data.derived_at}>{formatInstant(data.derived_at)}</time>. Reconhecer
          registra que você viu; o item permanece enquanto a condição existir.
        </p>
      </header>

      <div className="mt-4 flex flex-wrap gap-2" role="list" aria-label="Resumo por severidade">
        {(['critical', 'high', 'medium', 'low', 'info'] as const).map((sev) => (
          <span
            key={sev}
            role="listitem"
            className="rounded border border-line px-2 py-1 text-xs text-ink-muted"
          >
            {SEVERITY_LABEL[sev]}: <strong>{formatNumber(data.counts[sev] ?? 0)}</strong>
          </span>
        ))}
      </div>

      {naoVisiveis > 0 ? (
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-3 text-xs text-ink-muted">
          {formatNumber(naoVisiveis)} item(ns) existem e não aparecem aqui porque exigem autoridade
          que esta sessão não tem. Esconder que existem seria pior do que não poder resolvê-los.
        </p>
      ) : null}

      {data.items.length === 0 ? (
        <p className="mt-6 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhuma condição pede decisão agora. Esta caixa reflete o domínio: vazia significa vazia,
          e não significa que a leitura falhou.
        </p>
      ) : (
        <ul className="mt-6 space-y-3">
          {data.items.map((item) => (
            <li
              key={`${item.condition_type}:${item.condition_id}`}
              className={`rounded-card border border-line bg-card p-4 shadow-card ${SEVERITY_CLASS[item.severity] ?? ''}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">
                  {CONDITION_LABEL[item.condition_type] ?? item.condition_type}
                </span>
                <span className="text-xs uppercase tracking-wide text-ink-muted">
                  {SEVERITY_LABEL[item.severity] ?? item.severity}
                  {item.state === 'acknowledged' ? ' · reconhecido' : ''}
                </span>
              </div>

              <p className="mt-2">{item.summary}</p>

              {item.state === 'acknowledged' ? (
                <p className="mt-2 text-xs text-ink-faint">
                  Reconhecido por {item.acknowledged_by ?? 'ator não identificado'}
                  {item.acknowledged_at ? ` em ${formatInstant(item.acknowledged_at)}` : ''}. O item
                  continua aqui porque a condição continua.
                </p>
              ) : null}

              {item.due_at ? (
                <p className="mt-2 text-xs text-ink-muted">
                  Prazo: <time dateTime={item.due_at}>{formatInstant(item.due_at)}</time>
                </p>
              ) : null}

              <div className="mt-3 text-xs text-ink-muted">
                <span className="font-medium">O que pode ser feito:</span>{' '}
                {item.options.join(' · ')}
              </div>

              {item.condition_type === 'ContentItem' ? (
                <Link
                  to={`/conteudo/${item.condition_id}`}
                  className="mt-3 inline-block text-xs underline"
                >
                  Abrir o rastreio deste conteúdo
                </Link>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
